import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Button from '@amzn/awsui-components-console/button';
import Alert from '@amzn/awsui-components-console/alert';

import { GetWorkflowDefinitionResponse } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { AuthorizeAPIResponse } from 'src/@types/SNOWDataServiceTypes';
import { useAuthContext } from 'src/providers/AuthContext';
import { OLAF_WORKFLOW_BINDLE_DATA } from 'src/authentication/bindle-utils';
import {
	SNOW_NXT_WORKFLOW_ENDPOINT,
	SNOW_AUTOPOPULATION_SERVICE_ENDPOINT,
	SNOW_AUTOPOPULATION_SERVICE_REGION,
} from 'src/config';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { useJwtToken } from 'src/hooks/useJwtToken';
import useFetchOnUserActionUsingSDS from 'src/hooks/useFetchOnUserActionUsingSDS';
import ErrorLoader from 'src/components/commonComponents/errorLoader/errorLoader';

import LayoutEditor from 'src/components/LayoutEditor/LayoutEditor';
import { AvailableField, PortalLayout } from 'src/components/LayoutEditor/layoutSchema.types';
import { REVIEW_ONLY_FIELD_IDS, LAYOUT_EDITOR_HELP_PANEL, NO_WORKFLOW_SELECTED, SYSTEM_FIELD_IDS, isLayoutEligibleAttribute } from 'src/components/LayoutEditor/layoutConstants';
import { loadLayoutFromDefinition } from 'src/components/LayoutEditor/layoutStorageService';
import { AutoPopulationDefinitionMap } from 'src/components/LayoutEditor/layoutDependencyUtils';
import {
	filterTaskSpecificRequirements,
	filterWorkflowLevelRequirements,
} from 'src/components/selfServeWorkflows/requirementsPortal/requirementsFormGeneratorUtils';
import { withSubWorkflowInputs } from 'src/components/selfServeWorkflows/requirementsPortal/subWorkflowRequirementUtils';

export default function LayoutEditorPage({ setTools, hideTools, openTools }: any) {
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const workflowType = searchParams.get('workflowId') || '';
	const history = useHistory();
	const { userName } = useAuthContext();

	useEffect(() => {
		if (setTools) {
			setTools({
				title: <h2>{LAYOUT_EDITOR_HELP_PANEL.title}</h2>,
				content: (
					<SpaceBetween size="m">
						<Box variant="p">{LAYOUT_EDITOR_HELP_PANEL.description}</Box>
						<Box variant="h4">Steps</Box>
						<Box variant="p">{LAYOUT_EDITOR_HELP_PANEL.steps}</Box>
						<Box variant="h4">Sections</Box>
						<Box variant="p">{LAYOUT_EDITOR_HELP_PANEL.sections}</Box>
						<Box variant="h4">Fields</Box>
						<ul style={{ margin: 0, paddingLeft: '20px' }}>
							<li><strong>Available Fields</strong> — {LAYOUT_EDITOR_HELP_PANEL.fields.available}</li>
							<li><strong>Add to Section</strong> — {LAYOUT_EDITOR_HELP_PANEL.fields.addToSection}</li>
							<li><strong>Reorder</strong> — {LAYOUT_EDITOR_HELP_PANEL.fields.reorder}</li>
							<li><strong>Full Width</strong> — {LAYOUT_EDITOR_HELP_PANEL.fields.fullWidth}</li>
							<li><strong>Remove</strong> — {LAYOUT_EDITOR_HELP_PANEL.fields.remove}</li>
						</ul>
						<Box variant="h4">Preview</Box>
						<Box variant="p">{LAYOUT_EDITOR_HELP_PANEL.preview}</Box>
						<Box variant="h4">Additional Fields</Box>
						<Box variant="p">{LAYOUT_EDITOR_HELP_PANEL.additionalFields}</Box>
						<Box variant="h4">Saving</Box>
						<ul style={{ margin: 0, paddingLeft: '20px' }}>
							{LAYOUT_EDITOR_HELP_PANEL.saving.map((item, i) => <li key={i}>{item}</li>)}
						</ul>
					</SpaceBetween>
				),
				links: LAYOUT_EDITOR_HELP_PANEL.links,
			});
		}

		return () => {
			if (hideTools) hideTools();
		};
	}, [setTools, hideTools]);

	const {
		isLoading: isAuthLoading,
		error: authError,
		data: authResponse,
	} = useFetchForSDS<AuthorizeAPIResponse>({
		path: '/authorize',
		method: 'POST',
		body: {
			user: userName,
			resource: {
				...OLAF_WORKFLOW_BINDLE_DATA,
				action: 'edit',
				ids: [workflowType],
			},
			requestId: uuidv4(),
		},
	});

	const isAuthorized = authResponse?.authorizedResource?.ids?.includes(workflowType);
	const permissionDeniedError = authResponse && !isAuthorized
		? 'You do not have edit permissions for this workflow. Only workflow owners can customize the portal layout.'
		: null;

	const {
		result: {
			isLoading: isWorkflowDataLoading,
			error: workflowDataError,
			data: getWorkflowDefinitionData,
		},
		onClickCB: onAuthCompletionCB,
	} = useFetchOnUserActionUsingSDS<GetWorkflowDefinitionResponse>();

	// Separate call for persisting the layout so we don't clobber the GET result.
	const { onClickCB: updateWorkflowCB } = useFetchOnUserActionUsingSDS<any>();

	useEffect(() => {
		if (authResponse)
			onAuthCompletionCB({
				path: `/workflows/${workflowType}`,
				method: 'GET',
				url: SNOW_NXT_WORKFLOW_ENDPOINT,
			});
	}, [authResponse]);

	const { jwtToken, error: jwtTokenError } = useJwtToken();

	// Fetch AutoPopulation definitions to enforce the dependency graph in the editor.
	const { data: autoPopResponse } = useFetchForSDS<{ definition: AutoPopulationDefinitionMap }>({
		path: workflowType && isAuthorized && jwtToken
			? `/autoPopulation/definitions/${workflowType}`
			: '',
		method: 'GET',
		url: SNOW_AUTOPOPULATION_SERVICE_ENDPOINT,
		region: SNOW_AUTOPOPULATION_SERVICE_REGION,
		headers: jwtToken ? { 'x-jwt-token': jwtToken } : undefined,
	});
	const autoPopulationDefinitions: AutoPopulationDefinitionMap = autoPopResponse?.definition || {};

	const taskDefinitions = getWorkflowDefinitionData?.taskDefinitions || [];
	const workflowDefinition = getWorkflowDefinitionData?.workflowDefinition;
	const workflowName = workflowDefinition?.workflowDefinitionName || workflowType;

	// Load the existing portal layout from the workflow definition API response.
	// `portalLayout` is delivered as a stringified JSON blob; loadLayoutFromDefinition
	// handles parsing either the string or an already-inflated object.
	const initialLayout: PortalLayout | null = useMemo(
		() => loadLayoutFromDefinition(getWorkflowDefinitionData),
		[getWorkflowDefinitionData]
	);

	// The portal shows the fields of every sub-workflow in the tree, so the palette offers them too.
	// Inputs that already carry a value, such as one a parent supplied, are left out as in the portal.
	// Without sub-workflows these are just the workflow's tasks.
	const layoutTaskDefinitions = useMemo(
		() => withSubWorkflowInputs(taskDefinitions, getWorkflowDefinitionData?.subWorkflows).allTaskDefinitions,
		[taskDefinitions, getWorkflowDefinitionData?.subWorkflows]
	);

	const availableFields: AvailableField[] = useMemo(() => {
		const fields: AvailableField[] = [];

		const commonAttrs = new Map<string, any>();
		layoutTaskDefinitions.forEach(task => {
			filterWorkflowLevelRequirements(task.taskInputs).forEach(attr => {
				if (!isLayoutEligibleAttribute(attr)) return;
				if (attr.attributeId && !commonAttrs.has(attr.attributeId)) {
					commonAttrs.set(attr.attributeId, attr);
				}
			});
		});

		commonAttrs.forEach(attr => {
			fields.push({
				attributeId: attr.attributeId!,
				attributeName: attr.attributeName || attr.attributeId!,
				dataType: attr.dataType,
				description: attr.description,
				taskId: null,
				taskName: null,
				placed: false,
				reviewOnly: REVIEW_ONLY_FIELD_IDS.includes(attr.attributeId!),
			});
		});

		layoutTaskDefinitions.forEach(task => {
			filterTaskSpecificRequirements(task.taskInputs).forEach(attr => {
				if (!isLayoutEligibleAttribute(attr)) return;
				if (attr.attributeId) {
					const namespacedId = `${task.taskId}.${attr.attributeId}`;
					fields.push({
						attributeId: namespacedId,
						attributeName: attr.attributeName || attr.attributeId,
						dataType: attr.dataType,
						description: attr.description,
						taskId: task.taskId || null,
						taskName: task.taskName || null,
						placed: false,
					});
				}
			});
		});

		SYSTEM_FIELD_IDS.forEach(id => {
			if (!fields.some(f => f.attributeId === id)) {
				fields.push({
					attributeId: id,
					attributeName: id === 'EntityName' ? 'Entity Name' : id === 'EntityType' ? 'Entity Type' : id,
					dataType: 'STRING',
					description: undefined,
					taskId: null,
					taskName: null,
					placed: false,
					reviewOnly: REVIEW_ONLY_FIELD_IDS.includes(id),
				});
			}
		});

		return fields;
	}, [layoutTaskDefinitions]);

	// Persist the layout via UpdateWorkflowDefinitionAttributes (partial update).
	// Smithy contract: PATCH /workflows/{workflowId} with requestId in the header.
	// The server rejects the request with InvalidData when `workflowDefinitionAttributes`
	// is missing (it needs at least the current `version` for optimistic locking
	// plus the identifying attributes), so we echo the current attributes back
	// alongside the new portalLayout. `bindleId`, `createdAt`, `createdBy`, and
	// `derivedFromId` from WorkflowDefinition aren't part of the Attributes shape
	// and are stripped.
	const handleSave = async (layout: PortalLayout): Promise<{ ok: true } | { ok: false; error: string }> => {
		if (!workflowDefinition) {
			return { ok: false, error: 'Workflow definition is not loaded yet. Please retry in a moment.' };
		}

		const {
			workflowId,
			workflowDefinitionName,
			supportedTasks,
			description,
			entityType,
			status,
			domains,
			version,
			approvalRequiredToStartExecution,
			workflowApprovalMetadata,
			batchExecutionRequired,
			workflowIntention,
			isReRunAllowed,
		} = workflowDefinition;

		const workflowDefinitionAttributes = {
			workflowId,
			workflowDefinitionName,
			supportedTasks,
			description,
			entityType,
			status,
			domains,
			version,
			approvalRequiredToStartExecution,
			workflowApprovalMetadata,
			batchExecutionRequired,
			workflowIntention,
			isReRunAllowed,
			lastUpdatedBy: userName,
		};

		const response: any = await updateWorkflowCB({
			path: `/workflows/${workflowType}`,
			method: 'PATCH',
			url: SNOW_NXT_WORKFLOW_ENDPOINT,
			headers: { requestId: uuidv4() },
			body: {
				workflowDefinitionAttributes,
				portalLayout: JSON.stringify(layout),
			},
		});

		if (response && typeof response === 'object' && 'error' in response) {
			return { ok: false, error: (response as { error: string }).error };
		}
		return { ok: true };
	};

	const handleCancel = () => {
		history.goBack();
	};

	if (!workflowType) {
		return (
			<Container>
				<SpaceBetween size="l">
					<Header variant="h1">{NO_WORKFLOW_SELECTED.title}</Header>
					<Box>{NO_WORKFLOW_SELECTED.message}</Box>
					<Button href={NO_WORKFLOW_SELECTED.buttonHref}>{NO_WORKFLOW_SELECTED.buttonText}</Button>
				</SpaceBetween>
			</Container>
		);
	}

	if (authError || permissionDeniedError) {
		return <ErrorLoader isLoading={false} error={authError || permissionDeniedError} isAlert />;
	}

	if (isAuthLoading || isWorkflowDataLoading || !getWorkflowDefinitionData) {
		return <ErrorLoader isLoading={true} error={null} isAlert />;
	}

	if (workflowDataError) {
		return <ErrorLoader isLoading={false} error={workflowDataError} isAlert />;
	}

	return (
		<>
			{jwtTokenError && (
				<Alert type="warning" header="Auto-population definitions couldn't be loaded">
					{jwtTokenError}
				</Alert>
			)}
			<LayoutEditor
				workflowId={workflowType}
				workflowName={workflowName || ''}
				availableFields={availableFields}
				workflowIntention={workflowDefinition?.workflowIntention}
				autoPopulationDefinitions={autoPopulationDefinitions}
				initialLayout={initialLayout}
				openTools={openTools}
				onSave={handleSave}
				onCancel={handleCancel}
			/>
		</>
	);
}
